import { Component, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { ModalServiceService } from 'src/app/_services/modal-service.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { NgxSpinnerService } from 'ngx-spinner';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import moment from 'moment';

@Component({
  selector: 'app-pdf-viewer',
  standalone: false,
  templateUrl: './pdf-viewer.component.html',
  styleUrl: './pdf-viewer.component.scss'
})
export class PdfViewerComponent extends SubscriptionUtil implements OnInit, OnDestroy {

  translate: any;

  // Injected by ModalService — shape matches screenshot:
  // { pdfInfo: { id, fileName, fileUrl, generatedOn, version, createdBy, pdfTemplate, jobOrder, ... }, audits: [...] }
  data: any = null;

  safeFileUrl: SafeResourceUrl | null = null;
timezone:any;
dateFormat:any;
  constructor(
    public modalRef: BsModalRef,
    private languageTranslateService: LanguageTranslateService,
    private sanitizer: DomSanitizer,
    private modalService: ModalServiceService,
    private errorUtil: ErrorUtil,
    private spinner: NgxSpinnerService,
    private auth: AuthenticationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.timezone = this.auth.getUserTimezone();
      this.dateFormat = localStorage.getItem("date_format");

    console.log('PDF Viewer data:', this.data);
    this.getLanguageData();
    // data is injected by BsModalService before ngOnInit — safe to build URL here
    if (this.data?.pdfInfo?.fileUrl) {
      this.buildSafeUrl();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  getLanguageData(): void {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe(data => {
        if (Object.keys(data).length !== 0) {
          this.translate = data;
        }
      })
    );
  }

  // ─── Safe iframe URL ──────────────────────────────────────────────────
  private buildSafeUrl(): void {
    try {
      const url = this.data?.pdfInfo?.fileUrl;
      if (url && this.isRemote(url)) {
        // Append PDF viewer params to the raw URL before sanitizing
        const urlWithParams = url + '#toolbar=1&navpanes=0&scrollbar=1&zoom=80';
        this.safeFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlWithParams);
      } else {
        this.safeFileUrl = null;
      }
    } catch (e) {
      console.error('buildSafeUrl error:', e);
      this.safeFileUrl = null;
    }
  }

  isRemote(url?: string | null): boolean {
    return !!(url && (url.startsWith('http://') || url.startsWith('https://')));
  }

  isLocal(url?: string | null): boolean {
    return !!(url && !this.isRemote(url));
  }

  // ─── Display helpers ──────────────────────────────────────────────────
  get jobTitle(): string {
    const joNumber = this.data?.pdfInfo?.jobOrder?.jobOrderNumber || ('JO-' + this.data?.pdfInfo?.jobOrder?.id);
    const fileName = this.data?.pdfInfo?.fileName || this.data?.pdfInfo?.pdfTemplate?.templateName || 'PDF';
    return `${joNumber} – ${fileName}`;
  }

  get pdfVersion(): string {
    const v = this.data?.pdfInfo?.version;
    return v ? 'v' + v : '—';
  }

  get createdBy(): string {
    return this.data?.pdfInfo?.createdBy || '—';
  }

  get createdDate(): string {
  const date = this.data?.pdfInfo?.createdDate;
  if (!date) return '—';
  return moment(date).format(this.dateFormat ?? 'DD/MM/YYYY');
}
formatDate(date: any): string {
  if (!date) return '—';
  return moment(date).tz(this.timezone ?? moment.tz.guess()).format('hh:mm A, DD-MM-YYYY');
}

  get fileName(): string {
    return this.data?.pdfInfo?.fileName || this.data?.pdfInfo?.pdfTemplate?.templateName || '—';
  }

  get templateName(): string {
    return this.data?.pdfInfo?.pdfTemplate?.templateName || '—';
  }

  get audits(): any[] {
    return this.data?.audits ?? [];
  }

  get hasAuditTrail(): boolean {
    return this.audits.length > 0;
  }

  

  // ─── Actions ──────────────────────────────────────────────────────────
  async copyPath(): Promise<void> {
    const url = this.data?.pdfInfo?.fileUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch { /* silent */ }
  }

  openInNewTab(): void {
    const doc = this.data?.pdfInfo;
    
     if (!doc?.fileUrl) {
      this.errorUtil.setErrorMessage(400, null, 'No file URL available for this PDF', 'error', 3000);
      return;
    }

    const fileName = doc?.fileName || ('PDF-' + doc?.id + '.pdf');

    if (doc?.fileUrl?.startsWith('http://') || doc?.fileUrl?.startsWith('https://')) {
      this.spinner.show();
      fetch(doc?.fileUrl)
        .then(res => {
          if (!res.ok) throw new Error('Network response was not ok');
          return res.blob();
        })
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          window.URL.revokeObjectURL(url);
          this.spinner.hide();
        })
        .catch(() => {
          this.spinner.hide();
          window.open(doc.fileUrl!, '_blank', 'noopener,noreferrer');
        });
    } else {
      navigator.clipboard.writeText(doc.fileUrl).then(() => {
        this.errorUtil.setErrorMessage(200, 'File path copied to clipboard', null, 'success', 1000);
      });
    }

  }

  // ─── Email ───────────────────────────────────────────────────────────
  onEmailFromViewer(): void {
    const pdf = this.data?.pdfInfo;
    if (!pdf) return;
    this.modalRef.hide();
    this.modalService.openModal('documentsCta', {
      jobOrder: this.data?.jobOrder,
      pdf: pdf,
      type: 'email'
    });
  }

  // ─── Download (same logic as job-order-documents) ─────────────────────
  onDownload(): void {
    const url = this.data?.pdfInfo?.fileUrl;
    if (!url) {
      this.errorUtil.setErrorMessage(400, null, 'No file URL available for this PDF', 'error', 3000);
      return;
    }

    const fileName = this.data?.pdfInfo?.fileName || ('PDF-' + this.data?.pdfInfo?.id + '.pdf');

    if (url.startsWith('http://') || url.startsWith('https://')) {
      this.spinner.show();
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error('Network response was not ok');
          return res.blob();
        })
        .then(blob => {
          const objectUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = fileName;
          a.click();
          window.URL.revokeObjectURL(objectUrl);
          this.spinner.hide();
        })
        .catch(() => {
          this.spinner.hide();
          // Fallback: open in new tab if blob fetch fails (e.g. CORS)
          window.open(url, '_blank', 'noopener,noreferrer');
        });
    } else {
      // Local path — copy to clipboard
      navigator.clipboard.writeText(url).then(() => {
        this.errorUtil.setErrorMessage(200, 'File path copied to clipboard', null, 'success', 1000);
      });
    }
  }

  onClose(): void {
    this.modalRef.hide();
  }

  push(obs: any) {
    super.push(obs);
  }
}